import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import initialDashboardLayoutState, { DashboardLayoutState } from './dashboard-layout-initial-state';
import { DashboardLayout, DashboardWidgetId, DashboardWidgetLayout } from '../models/dashboard-layout.model';
import { getLayoutBottom, normalizeCustomLayout } from '../utils/dashboard-layout.utils';

export type DashboardWidgetPosition = Pick<DashboardWidgetLayout, 'id' | 'x' | 'y' | 'w' | 'h'>;

// Position and visibility edits only apply to a custom layout; entering edit mode on a default
// first stores the effective default as the custom layout (see setLayout).
const updateCustomWidgets = (
    state: DashboardLayoutState,
    update: (widgets: DashboardWidgetLayout[]) => DashboardWidgetLayout[]
): DashboardLayoutState => {
    const layout = normalizeCustomLayout(state.custom);
    if (!layout) return { custom: null };
    return { custom: normalizeCustomLayout({ ...layout, widgets: update(layout.widgets) }) };
};

const dashboardLayoutSlice = createSlice({
    name: 'dashboardLayoutSlice',
    initialState: initialDashboardLayoutState,
    reducers: {
        setWidgetPositions(state, { payload }: PayloadAction<DashboardWidgetPosition[]>) {
            const positions = new Map(payload.map(position => [position.id, position]));
            return updateCustomWidgets(state, widgets => widgets.map(widget => {
                const position = positions.get(widget.id);
                return position && widget.visible ? { ...widget, x: position.x, y: position.y, w: position.w, h: position.h } : widget;
            }));
        },
        setWidgetVisibility(state, { payload }: PayloadAction<{ id: DashboardWidgetId; visible: boolean }>) {
            return updateCustomWidgets(state, widgets => {
                const bottom = getLayoutBottom(widgets);
                return widgets.map(widget => {
                    if (widget.id !== payload.id || widget.visible === payload.visible) return widget;
                    return payload.visible ? { ...widget, visible: true, x: 0, y: bottom } : { ...widget, visible: false };
                });
            });
        },
        setLayout(state, { payload }: PayloadAction<DashboardLayout>) {
            return { custom: normalizeCustomLayout(payload) };
        },
        resetLayout() {
            return { custom: null };
        }
    }
});

export const {
    setWidgetPositions,
    setWidgetVisibility,
    setLayout,
    resetLayout
} = dashboardLayoutSlice.actions;

export default dashboardLayoutSlice.reducer;
